import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { Scroll3DSection, Tilt3D } from './Scroll3D';
import {
  Building2,
  Globe,
  Zap,
  Star,
  ArrowRight,
  TrendingUp,
  Users,
  Award,
  Shield,
  Code,
  Database,
  Cpu,
  Brain,
  Smartphone,
  Cloud,
  Lock,
  BarChart3,
  Wifi,
  Heart,
  GraduationCap,
  DollarSign,
  Droplets,
  Building,
  BookOpen
} from 'lucide-react';

const Sectors = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const sectors = [
    {
      title: t('sectors.financial.title'),
      description: t('sectors.financial.description'),
      icon: DollarSign,
      color: 'from-neon-green to-neon-cyan',
      features: [
        t('sectors.financial.features.0'),
        t('sectors.financial.features.1'),
        t('sectors.financial.features.2'),
        t('sectors.financial.features.3'),
        t('sectors.financial.features.4'),
        t('sectors.financial.features.5'),
      ],
      stats: t('sectors.financial.stats'),
    },
    {
      title: t('sectors.oilGas.title'),
      description: t('sectors.oilGas.description'),
      icon: Droplets,
      color: 'from-neon-cyan to-neon-blue',
      features: [
        t('sectors.oilGas.features.0'),
        t('sectors.oilGas.features.1'),
        t('sectors.oilGas.features.2'),
        t('sectors.oilGas.features.3'),
        t('sectors.oilGas.features.4'),
        t('sectors.oilGas.features.5'),
      ],
      stats: t('sectors.oilGas.stats'),
    },
    {
      title: t('sectors.government.title'),
      description: t('sectors.government.description'),
      icon: Building,
      color: 'from-neon-blue to-neon-purple',
      features: [
        t('sectors.government.features.0'),
        t('sectors.government.features.1'),
        t('sectors.government.features.2'),
        t('sectors.government.features.3'),
        t('sectors.government.features.4'),
        t('sectors.government.features.5'),
      ],
      stats: t('sectors.government.stats'),
    },
    {
      title: t('sectors.healthcare.title'),
      description: t('sectors.healthcare.description'),
      icon: Heart,
      color: 'from-neon-purple to-neon-green',
      features: [
        t('sectors.healthcare.features.0'),
        t('sectors.healthcare.features.1'),
        t('sectors.healthcare.features.2'),
        t('sectors.healthcare.features.3'),
        t('sectors.healthcare.features.4'),
        t('sectors.healthcare.features.5'),
      ],
      stats: t('sectors.healthcare.stats'),
    },
    {
      title: t('sectors.education.title'),
      description: t('sectors.education.description'),
      icon: GraduationCap,
      color: 'from-neon-green to-neon-cyan',
      features: [
        t('sectors.education.features.0'),
        t('sectors.education.features.1'),
        t('sectors.education.features.2'),
        t('sectors.education.features.3'),
        t('sectors.education.features.4'),
        t('sectors.education.features.5'),
      ],
      stats: t('sectors.education.stats'),
    },
    {
      title: t('sectors.technology.title'),
      description: t('sectors.technology.description'),
      icon: Cpu,
      color: 'from-neon-cyan to-neon-blue',
      features: [
        t('sectors.technology.features.0'),
        t('sectors.technology.features.1'),
        t('sectors.technology.features.2'),
        t('sectors.technology.features.3'),
        t('sectors.technology.features.4'),
        t('sectors.technology.features.5'),
      ],
      stats: t('sectors.technology.stats'),
    },
  ];

  return (
    <section id="sectors" ref={ref} className="py-10 bg-transparent dark:bg-transparent relative overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-40 h-40 bg-gradient-to-r from-neon-green/15 to-neon-cyan/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-gradient-to-r from-neon-blue/15 to-neon-purple/15 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-gradient-to-r from-neon-green/8 to-neon-purple/8 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 right-1/4 w-32 h-32 bg-gradient-to-r from-neon-cyan/10 to-neon-blue/10 rounded-full blur-2xl"></div>
      </div>

      <Scroll3DSection className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Enhanced Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-flex items-center space-x-3 rtl:space-x-reverse bg-gradient-to-r from-neon-green/20 to-neon-cyan/20 backdrop-blur-xl border border-neon-green/40 rounded-full px-8 py-4 mb-8 shadow-2xl shadow-neon-green/20"
          >
            <Globe className="w-6 h-6 text-neon-green" />
            <span className="text-neon-green font-bold text-lg">{t('sectors.badge')}</span>
            <Star className="w-5 h-5 text-neon-cyan" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-8"
          >
            <span className="gradient-text-neon drop-shadow-2xl">{t('sectors.title')}</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed font-light"
          >
            {t('sectors.subtitle')}
          </motion.p>
        </motion.div>

        {/* Enhanced Sectors Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
        >
          {sectors.map((sector, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="w-full h-full"
            >
              <Tilt3D className="w-full h-full">
                <div className="group relative bg-black/50 backdrop-blur-xl border border-neon-green/30 rounded-3xl p-8 hover:border-neon-green/50 transition-all duration-500 card-hover-neon shadow-2xl shadow-neon-green/10 flex flex-col h-full">
                  {/* Enhanced Sector Icon */}
                  <div className={`relative w-20 h-20 bg-gradient-to-r ${sector.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-2xl`}>
                    <sector.icon className="w-10 h-10 text-black" />
                    <div className="absolute -inset-2 bg-gradient-to-r from-neon-green to-neon-cyan rounded-2xl blur opacity-40 group-hover:opacity-60 transition-opacity duration-300"></div>
                  </div>

                  {/* Enhanced Sector Content */}
                  <h3 className="text-2xl font-black text-white mb-4 group-hover:text-neon-green transition-colors duration-300">
                    {sector.title}
                  </h3>
                  <p className="text-white/70 mb-6 leading-relaxed text-base flex-grow">
                    {sector.description}
                  </p>
                  {/* Enhanced Features List */}
                  <ul className="space-y-3 mb-6">
                    {sector.features.map((feature, featureIndex) => (
                      <li
                        key={featureIndex}
                        className="flex items-center space-x-3 rtl:space-x-reverse text-white/80 group-hover:text-white/90 transition-colors duration-300"
                      >
                        <div className="w-2 h-2 bg-gradient-to-r from-neon-green to-neon-cyan rounded-full flex-shrink-0 shadow-lg shadow-neon-green/50"></div>
                        <span className="text-sm font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {/* Enhanced Stats Badge */}
                  <div className="mb-6">
                    <span className="inline-block bg-gradient-to-r from-neon-green/20 to-neon-cyan/20 text-neon-green font-bold text-sm px-4 py-2 rounded-full border border-neon-green/30">
                      {sector.stats}
                    </span>
                  </div>
                </div>
              </Tilt3D>
            </motion.div>
          ))}
        </motion.div>
      </Scroll3DSection>
    </section>
  );
};

export default Sectors; 